// Student profile — templateID 4 (Students) on the old backend.
//
// Field-slot mapping confirmed from the old app's Register.js (write) and
// Myprofile.js (read) for _1.._18 (read index = write "_N" + 2, e.g. _15
// Aadhar Number reads back at row index 17), AND directly verified against a
// live response for the full field range — see
// docs/BACKEND_SPEC_MISSING_ENDPOINTS.md ("Students template field mapping
// was wrong"). This template has EXACTLY 28 registered fields (_1.._28);
// _29+ don't exist and are silently dropped, same pattern as Courses.
//
// IMPORTANT, already-fixed bug: this schema previously assumed _21.._28
// were free slots for bloodGroup/emergencyName/emergencyPhone/
// emergencyRelation/medicalConditions/insurance (and dietaryDetails/
// nextOfKinName/status past the real field count). They are NOT free — _21
// through _28 are real, already-in-use fields (NextOfKin, HeightinCM,
// WeightinKG, BMI, ShoeSize, AcademicQualification, Hobbies, NextOfKin
// Address&Telephone). Every previous write to bloodGroup/emergencyName/
// emergencyPhone/emergencyRelation/medicalConditions/insurance was actually
// overwriting a real student's NextOfKin/Height/Weight/BMI/ShoeSize/
// NextOfKinAddress data with mismatched values. Fixed here to write/read the
// REAL fields under their real names; there is currently no home for blood
// group, emergency contact, medical conditions, insurance, dietary details,
// a separate "next of kin name", or a status flag — none of those exist on
// this template. See the backend spec doc for what to register if these are
// still wanted as student-profile fields.
import {
  addUpdateTemplateDataWeb, bulkImportTemplateData, cell, deleteTemplateData,
  getDynamicTemplateList, getMaxRecordidInTemplate, getTemplateDataList, isApiConnected,
  namedCell, type CrmNamedCell, type TemplateRow,
} from "@/lib/apiClient";

const STUDENTS_TEMPLATE_ID = 4;

const SCHEMA = {
  id: 0,
  createDt: 1,
  studentCode: 3,
  avatarUrl: 4,
  fullName: 5,
  phone: 6,
  alternatePhone: 7,
  email: 8,
  gender: 10,
  dob: 11,
  address: 12,
  city: 13,
  state: 14,
  pincode: 15,
  country: 16,
  aadharNumber: 17,
  nri: 18,
  nationality: 19,
  fatherName: 20,
  age: 22,
  nextOfKin: 23,
  heightCm: 24,
  weightKg: 25,
  bmi: 26,
  shoeSize: 27,
  academicQualification: 28,
  hobbies: 29,
  nextOfKinAddressPhone: 30,
} as const;

export interface StudentProfileRow {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  alternatePhone: string;
  dob: string;
  age: string;
  gender: string;
  fatherName: string;
  aadharNumber: string;
  nationality: string;
  nri: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  academicQualification: string;
  hobbies: string;
  avatarUrl: string;
  joinedDate: string;
  // Real fields this template has, but the current profile UI doesn't
  // collect/display yet (see the module comment above).
  nextOfKin: string;
  heightCm: string;
  weightKg: string;
  bmi: string;
  shoeSize: string;
  nextOfKinAddressPhone: string;
  // No real backend field exists for any of these (see module comment) —
  // always empty. Kept so callers/UI don't need reshaping; a future
  // registration of new fields (see backend spec doc) would slot in here.
  bloodGroup: string;
  emergencyName: string;
  emergencyPhone: string;
  emergencyRelation: string;
  medicalConditions: string;
  insurance: string;
  dietaryDetails: string;
  nextOfKinName: string;
  status: string;
}

export function transformRow(row: TemplateRow[]): StudentProfileRow {
  return {
    id: cell(row, SCHEMA.id),
    fullName: cell(row, SCHEMA.fullName),
    email: cell(row, SCHEMA.email),
    phone: cell(row, SCHEMA.phone),
    alternatePhone: cell(row, SCHEMA.alternatePhone),
    dob: cell(row, SCHEMA.dob),
    age: cell(row, SCHEMA.age),
    gender: cell(row, SCHEMA.gender),
    fatherName: cell(row, SCHEMA.fatherName),
    aadharNumber: cell(row, SCHEMA.aadharNumber),
    nationality: cell(row, SCHEMA.nationality),
    nri: cell(row, SCHEMA.nri) || "No",
    address: cell(row, SCHEMA.address),
    city: cell(row, SCHEMA.city),
    state: cell(row, SCHEMA.state),
    pincode: cell(row, SCHEMA.pincode),
    academicQualification: cell(row, SCHEMA.academicQualification),
    hobbies: cell(row, SCHEMA.hobbies),
    avatarUrl: cell(row, SCHEMA.avatarUrl),
    joinedDate: cell(row, SCHEMA.createDt),
    nextOfKin: cell(row, SCHEMA.nextOfKin),
    heightCm: cell(row, SCHEMA.heightCm),
    weightKg: cell(row, SCHEMA.weightKg),
    bmi: cell(row, SCHEMA.bmi),
    shoeSize: cell(row, SCHEMA.shoeSize),
    nextOfKinAddressPhone: cell(row, SCHEMA.nextOfKinAddressPhone),
    bloodGroup: "",
    emergencyName: "",
    emergencyPhone: "",
    emergencyRelation: "",
    medicalConditions: "",
    insurance: "",
    dietaryDetails: "",
    nextOfKinName: "",
    status: "active",
  };
}

// /api/getTemplateDataList rejects ALL reads for the Students template on
// the live backend — confirmed regardless of filters, always returning an
// empty result — apparently a deliberate restriction on credential-bearing
// templates (Students=4, Admins=7) in the generic content API.
//
// BUT /dynamicApp/getDynamicTemplateList (TypeName: "Students") does NOT
// have this restriction — confirmed live 2026-09-28, same endpoint the old
// app's own Invoicenew.js used (fun_Login) to look up an arbitrary student
// by email for its invoice page. This is the real, working path for an
// admin to read ANY student's full profile — not just the CRM module
// (getCrmTemplateDataList), which needs a separate, currently-expired
// VITE_API_CRM_AUTH_TOKEN (see docs/BACKEND_SPEC_MISSING_ENDPOINTS.md).
//
// Its rows are named cells ("Full Name", "DOB", "Permanent Address", ...),
// not the positional _1../_28 shape SCHEMA above maps — mapped by label
// here instead, tolerant of the backend's inconsistent trailing-tab field
// names (e.g. "HeightinCM\t") via apiClient.namedCell().
function transformNamedRow(row: CrmNamedCell[]): StudentProfileRow {
  return {
    id: namedCell(row, "_id"),
    fullName: namedCell(row, "Full Name"),
    email: namedCell(row, "Email"),
    phone: namedCell(row, "Mobile"),
    alternatePhone: namedCell(row, "Alternate Mobile Number"),
    dob: namedCell(row, "DOB"),
    age: namedCell(row, "Age"),
    gender: namedCell(row, "Gender"),
    fatherName: namedCell(row, "Father's Name"),
    aadharNumber: namedCell(row, "Aadhar Number"),
    nationality: namedCell(row, "Nationality"),
    nri: namedCell(row, "NRI") || "No",
    address: namedCell(row, "Permanent Address"),
    city: namedCell(row, "City"),
    state: namedCell(row, "State"),
    pincode: namedCell(row, "Pincode"),
    academicQualification: namedCell(row, "AcademicQualification"),
    hobbies: namedCell(row, "AnySpecialQualificationOrHobbies"),
    avatarUrl: namedCell(row, "Profile Pic"),
    joinedDate: namedCell(row, "createDt"),
    nextOfKin: namedCell(row, "NextofKin"),
    heightCm: namedCell(row, "HeightinCM"),
    weightKg: namedCell(row, "WeightinKG"),
    bmi: namedCell(row, "BMI"),
    shoeSize: namedCell(row, "ShoeSize"),
    nextOfKinAddressPhone: namedCell(row, "NextofKinWithAddress&Telephone"),
    bloodGroup: "",
    emergencyName: "",
    emergencyPhone: "",
    emergencyRelation: "",
    medicalConditions: "",
    insurance: "",
    dietaryDetails: "",
    nextOfKinName: "",
    status: "active",
  };
}

const PROFILE_CACHE_KEY = "nimas_student_profile_cache";

export function cacheStudentProfile(profile: StudentProfileRow): void {
  try {
    localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
  } catch {
    // Storage unavailable — profile prefill/self-view will just miss the cache fallback
  }
}

export function clearCachedStudentProfile(): void {
  localStorage.removeItem(PROFILE_CACHE_KEY);
}

function getCachedStudentProfile(email: string): StudentProfileRow | null {
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw) as StudentProfileRow;
    return cached.email?.toLowerCase() === email.toLowerCase() ? cached : null;
  } catch {
    return null;
  }
}

export async function getStudentProfileByEmail(email: string): Promise<StudentProfileRow | null> {
  if (!isApiConnected() || !email) return null;

  const { response } = await getTemplateDataList(STUDENTS_TEMPLATE_ID, { Email: email }, { pgSize: 1 });
  const row = response.records?.[0];
  if (row) return transformRow(row);

  // The positional read above is expected to come back empty (see the
  // module comment) — fall back to the working named-cell endpoint before
  // giving up to the cache.
  try {
    const dynamicResp = await getDynamicTemplateList("Students", { Email: email }, { pgSize: 1 });
    const namedRow = dynamicResp.response?.records?.[0];
    if (namedRow) return transformNamedRow(namedRow);
  } catch {
    // Network/backend failure on this fallback path — fall through to cache below.
  }

  return getCachedStudentProfile(email);
}

export interface StudentProfileUpdate {
  fullName?: string;
  email?: string;
  phone?: string;
  dob?: string;
  gender?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  avatarUrl?: string;
  // No real backend field exists for these (see module comment) — accepted
  // for now so the profile form doesn't need reshaping, but silently
  // ignored on save rather than corrupting a different real field.
  bloodGroup?: string;
  emergencyName?: string;
  emergencyPhone?: string;
  emergencyRelation?: string;
  medicalConditions?: string;
  status?: string;
}

const FIELD_TO_SLOT: Partial<Record<keyof StudentProfileUpdate, keyof typeof SCHEMA>> = {
  fullName: "fullName",
  email: "email",
  phone: "phone",
  dob: "dob",
  gender: "gender",
  address: "address",
  city: "city",
  state: "state",
  pincode: "pincode",
  avatarUrl: "avatarUrl",
  // bloodGroup/emergencyName/emergencyPhone/emergencyRelation/
  // medicalConditions/status intentionally omitted — see the module
  // comment at the top of this file; there's no real slot for them.
};

export async function saveStudentProfile(id: string | null, updates: StudentProfileUpdate): Promise<StudentProfileRow | null> {
  const fields: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(updates)) {
    const slotKey = FIELD_TO_SLOT[key as keyof StudentProfileUpdate];
    if (!slotKey) continue;
    const writeIndex = SCHEMA[slotKey] - 2; // read index -> write "_N"
    fields[`_${writeIndex}`] = value ?? "";
  }

  // Confirmed live: updates (with `_id`) merge onto the existing row and
  // preserve every field not included here — unlike creates, a partial
  // update does NOT shift/corrupt other fields, so it's safe to send only
  // what changed. (Creates are a different story — see createStudent.)
  const resp = await addUpdateTemplateDataWeb(STUDENTS_TEMPLATE_ID, fields, id ?? undefined);
  if (resp.hasErrors) throw new Error(resp.errors || "Failed to save profile");

  // getStudentProfileByEmail's live read is blocked for this template (see
  // its own comment) and would otherwise return stale cached data right
  // after a save — merge the write directly into the cache instead so the
  // caller sees what it just saved.
  if (updates.email) {
    const existing = getCachedStudentProfile(updates.email) ?? (await getStudentProfileByEmail(updates.email));
    const merged: StudentProfileRow = { ...(existing ?? ({} as StudentProfileRow)), ...updates, id: id ?? existing?.id ?? "" };
    cacheStudentProfile(merged);
    return merged;
  }
  return null;
}

// ─── Admin CRUD ───
// NOTE: booking-count-per-student and cascade-delete-of-bookings are omitted
// here — both require reading/writing the old Bookings templateID 5, which
// per the "get the schema first" decision isn't touched yet. Deleting a
// student here removes the student row only; any related bookings become
// orphaned until the booking pipeline migrates and a proper cascade can be
// added.

// NOTE: unlike updates, CREATES were confirmed (on Courses, same backend
// pattern — see docs/BACKEND_SPEC_MISSING_ENDPOINTS.md) to omit any field not
// included in the write from the row's stored array entirely, which shifts
// every subsequent field's read position. This function only sends whatever
// the admin "Add Student" form actually collected, so a new student created
// with several fields left blank risks the same shift/corruption Courses had
// — flagged as a follow-up (would need the same "always send every field up
// to the highest index" treatment createCourse got), not yet fixed here
// since this session's scope was the field-mismapping bug, not this one.
export async function createStudent(updates: StudentProfileUpdate & { password?: string }): Promise<void> {
  const fields: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(updates)) {
    if (key === "password") continue;
    const slotKey = FIELD_TO_SLOT[key as keyof StudentProfileUpdate];
    if (!slotKey) continue;
    const writeIndex = SCHEMA[slotKey] - 2;
    fields[`_${writeIndex}`] = value ?? "";
  }
  const { response: maxIdResp } = await getMaxRecordidInTemplate(STUDENTS_TEMPLATE_ID);
  fields._1 = maxIdResp?.maxid ?? 1;
  fields._7 = updates.password || "";
  fields._19 = "isStudent";
  await addUpdateTemplateDataWeb(STUDENTS_TEMPLATE_ID, fields);
}

export async function deleteStudents(ids: string[]): Promise<void> {
  await deleteTemplateData(STUDENTS_TEMPLATE_ID, ids);
}

export async function updateStudentStatus(_id: string, _status: string): Promise<void> {
  // No real "status" field exists on this template (see module comment) —
  // throw so the caller's existing error handling surfaces this honestly
  // instead of silently no-op'ing (or, before this fix, silently corrupting
  // the real NextOfKinAddress field that used to be mismapped as "status").
  throw new Error("Activating/deactivating students isn't supported by the current backend schema yet.");
}

export async function bulkImportStudents(
  rows: (StudentProfileUpdate & { password?: string })[],
): Promise<{ insertedCount: number; updatedCount: number; skippedCount: number }> {
  const toFields = (r: StudentProfileUpdate & { password?: string }) => {
    const fields: Record<string, unknown> = { _19: "isStudent" };
    for (const [key, value] of Object.entries(r)) {
      if (key === "password") { fields._7 = value; continue; }
      const slotKey = FIELD_TO_SLOT[key as keyof StudentProfileUpdate];
      if (!slotKey) continue;
      fields[`_${SCHEMA[slotKey] - 2}`] = value ?? "";
    }
    return fields;
  };
  const { response } = await bulkImportTemplateData(
    STUDENTS_TEMPLATE_ID,
    rows.map(toFields),
    { onConflictField: "_6", upsert: false },
  );
  return response;
}
